<template>
    <!-- Outer shell is the icon-box anchor; body translates by the declutter offset.
         pointer-events plumbing matches Go web SubjectMarker: the shell is transparent
         in callout mode so the empty anchor box doesn't steal clicks; the body stays
         hittable. -->
    <div class="fleet-pin"
         :class="{'fleet-pin--callout': isCallout, 'fleet-pin--approx': isApprox}"
         :title="isApprox ? 'Approximate location, waiting for first GPS fix' : undefined">
        <div class="fleet-pin__body" :style="bodyStyle">
            <!-- Followed-device persistent ring + highlight pulse -->
            <span v-if="tracked" class="fleet-pin__track" :style="ringStyle"/>
            <span v-if="tracked || highlighted" class="fleet-pin__pulse" :style="ringStyle"/>
            <!-- Breathing glow while moving -->
            <span v-if="isMoving" class="fleet-pin__glow" :style="glowStyle"/>

            <!-- Shape: bulb + tail/quill. One drop-shadow filter traces the combined
                 white silhouette for a seamless single border - no seam at the weld. -->
            <div class="fleet-pin__shape">
                <!-- Circle (normal) or rounded-square (callout) white bulb -->
                <div class="fleet-pin__bulb" :class="{'fleet-pin__bulb--square': isCallout, 'fleet-pin__bulb--breathing': isMoving}">
                    <img v-if="photo && !photoFailed" :src="photo" class="fleet-pin__photo"
                         @error="photoFailed = true"/>
                    <div v-else class="fleet-pin__fallback" :style="{backgroundColor: markerColor}">
                        <span :style="initialsStyle">{{ initials }}</span>
                    </div>
                </div>
                <!-- Normal pin: CSS downward triangle whose tip is the GPS anchor -->
                <span v-if="!isCallout" class="fleet-pin__tail"/>
                <!-- Callout: SVG curved quill rotated toward the GPS anchor (continuous
                     angle, same path as Go web SubjectMarker.vue) -->
                <svg v-else class="fleet-pin__svgptr" viewBox="-40 -40 80 80" width="80" height="80">
                    <path :d="PTR_PATH" fill="#ffffff" :transform="pointerTransform"/>
                </svg>
            </div>
            <!-- Status dot: coloured circle top-right of the bulb -->
            <span class="fleet-pin__dot" :style="dotStyle"/>
        </div>
        <!-- Asset name floats above moving markers -->
        <div v-if="device.motion_state" class="fleet-pin__name">{{ device.asset_label }}</div>
    </div>
</template>

<script setup>
import {computed, ref} from "vue";
import {isApproximate} from "@/composables/fleetStatus";

const props = defineProps({
    device:      {type: Object,  required: true},
    // Pixel offset from the collision-declutter engine ({x:0,y:0} = normal pin).
    offset:      {type: Object,  default: () => ({x: 0, y: 0})},
    tracked:     {type: Boolean, default: false},
    highlighted: {type: Boolean, default: false},
});

// Geometry — keep in sync with iconSize/iconAnchor in DevicesMap.vue.
// BULB is the diameter of the circle/square; ANCHOR_Y is the tail tip (GPS point).
const BULB     = 36;
const ANCHOR_X = 18;   // icon-box centre (BULB / 2)
const ANCHOR_Y = 44;   // tail tip y in icon-box coords

const off = computed(() => ({
    x: Number(props.offset?.x) || 0,
    y: Number(props.offset?.y) || 0,
}));

// Distance from the current (offset) bulb centre to where the GPS anchor would
// be at zero offset. Tiny offsets (<5px) render as a normal pin to avoid a
// degenerate near-zero callout on identical-position devices.
const offLen = computed(() =>
    Math.hypot(off.value.x, off.value.y - (ANCHOR_Y - BULB / 2)));

const isCallout = computed(() =>
    (off.value.x !== 0 || off.value.y !== 0) && offLen.value >= 5);

const bodyStyle = computed(() =>
    isCallout.value ? {transform: `translate(${off.value.x}px, ${off.value.y}px)`} : {});

// Continuous pointer angle: vector from the (offset) bulb centre to the GPS
// anchor. In body-local coords the anchor sits at (ANCHOR_X - off.x,
// ANCHOR_Y - BULB/2 - off.y). The quill path points along +x at angle 0.
const pointerAngle = computed(() => {
    const dx = -off.value.x;
    const dy = (ANCHOR_Y - BULB / 2) - off.value.y;
    return Math.atan2(dy, dx) * 180 / Math.PI;
});
const pointerTransform = computed(() => `rotate(${pointerAngle.value} 0 0)`);
// Curved quill: base tucked under the bulb, tapered tip points outward.
// Same path as Go web SubjectMarker.vue.
const PTR_PATH = "M 0,-11 Q 24,-7 36,0 Q 24,7 0,11 Z";

// Photo: driver photo when a driver is assigned, nothing otherwise (initials
// fallback). The asset TYPE photo is a generic illustration and is not used.
const photoFailed = ref(false);
const driverName = computed(() => {
    const f = props.device.driver_first_name || '';
    const l = props.device.driver_last_name  || '';
    return `${f} ${l}`.trim();
});
const displayName = computed(() => driverName.value || props.device.asset_label || props.device.slno || '');
const photo = computed(() => {
    const hasDriver = !!(props.device.driver_first_name || props.device.driver_last_name);
    return hasDriver ? (props.device.driver_photo || '') : '';
});
const initials = computed(() => {
    const parts = displayName.value.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    const f = s => (s ? s[0].toUpperCase() : '');
    return parts.length === 1 ? f(parts[0]) : f(parts[0]) + f(parts[parts.length - 1]);
});

// Colour used for initials background and motion/status indicators.
const markerColor = computed(() =>
    props.device.asset_color || props.device.status_color || '#1976D2');

const isMoving = computed(() => !!props.device.motion_state);

// Activation seed (approximate position), not a real GPS fix: drawn faded.
const isApprox = computed(() => isApproximate(props.device));

const statusColor = computed(() => {
    const sc = props.device.status_color;
    if (sc === 'red')   return '#EF4444';  // offline / non-reporting
    if (props.device.motion_state) return '#2563EB'; // moving/idling = blue
    return '#F59E0B';  // parked / stationary = amber
});

const initialsStyle = computed(() => ({
    color:      '#ffffff',
    fontWeight: 700,
    fontSize:   `${Math.round(BULB * 0.36)}px`,
    lineHeight: 1,
}));
const dotStyle  = computed(() => ({backgroundColor: statusColor.value}));
const glowStyle = computed(() => ({'--glow': statusColor.value}));
const ringStyle = computed(() => ({borderColor: statusColor.value}));
</script>

<style scoped>
/* Icon-box shell — same size as iconSize in DevicesMap.vue. */
.fleet-pin {
    position: relative;
    width: 36px;
    height: 48px;
}

/* Translatable body (bulb + rings + dot). Transitions the declutter nudge. */
.fleet-pin__body {
    position: absolute;
    top: 0; left: 0;
    width: 36px; height: 48px;
    transition: transform 120ms ease;
}

/* Activation seed (approximate position), not a real fix: the whole pin is
   faded until the first real GPS fix replaces it. */
.fleet-pin--approx .fleet-pin__body {
    opacity: 0.55;
}

/* In callout mode the icon-box shell is transparent so its empty area doesn't
   block map clicks; only the visible bulb body remains hittable. */
.fleet-pin--callout {
    pointer-events: none;
}
.fleet-pin--callout .fleet-pin__body {
    pointer-events: auto;
}

/* Shape wrapper: single drop-shadow filter traces the combined white silhouette
   (bulb + tail/quill) for one continuous border with no seam at the weld.
   Mirrors the Go web SubjectMarker .subject-pin__shape filter. */
.fleet-pin__shape {
    position: absolute;
    top: 0; left: 0;
    width: 36px; height: 48px;
    z-index: 3;
    filter:
        drop-shadow(1px 0 0 rgba(80, 80, 80, 0.95))
        drop-shadow(-1px 0 0 rgba(80, 80, 80, 0.95))
        drop-shadow(0 1px 0 rgba(80, 80, 80, 0.95))
        drop-shadow(0 -1px 0 rgba(80, 80, 80, 0.95))
        drop-shadow(0 2px 3px rgba(0, 0, 0, 0.30));
}

/* White bulb: circle (normal pin) or rounded-square (callout).
   2.5px white border so the edge welds cleanly to the white tail/quill. */
.fleet-pin__bulb {
    position: absolute;
    top: 0; left: 0;
    width: 36px; height: 36px;
    box-sizing: border-box;
    border: 2.5px solid #ffffff;
    border-radius: 50%;
    background: #ffffff;
    overflow: hidden;
    z-index: 3;
}
.fleet-pin__bulb--square {
    border-radius: 10px;
}

/* Breathing size pulse while the device is moving.
   Expands over 1s (0–33%), contracts over 2s (33–100%).
   CSS transform only — layout and Leaflet anchor are unaffected. */
.fleet-pin__bulb--breathing {
    animation: fleet-breathe 3s ease-in-out infinite;
    transform-origin: center;
}
@keyframes fleet-breathe {
    0%   { transform: scale(1);    }
    33%  { transform: scale(1.08); }
    100% { transform: scale(1);    }
}

.fleet-pin__photo {
    width: 100%; height: 100%;
    object-fit: cover;
    display: block;
}

.fleet-pin__fallback {
    width: 100%; height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* CSS downward triangle for the normal pin. top=30 overlaps the circle bottom
   by 6px for a seamless white weld; tip lands at y=44 = iconAnchor[1]. */
.fleet-pin__tail {
    position: absolute;
    left: 50%;
    top: 30px;
    transform: translateX(-50%);
    width: 0; height: 0;
    border-left:  8px solid transparent;
    border-right: 8px solid transparent;
    border-top:   14px solid #ffffff;
    z-index: 2;
}

/* SVG quill: centred on the bulb (viewBox -40,-40 puts 0,0 at the bulb centre
   at 18,18); rotated by pointerAngle to aim at the GPS anchor.
   z-index below the bulb so its base tucks under the square's rounded corners. */
.fleet-pin__svgptr {
    position: absolute;
    top:  -22px;  /* 18 (bulb centre y) - 40 (half of 80px viewBox) */
    left: -22px;  /* 18 (bulb centre x) - 40 */
    width: 80px; height: 80px;
    overflow: visible;
    z-index: 2;
    pointer-events: none;
}

/* Status dot: coloured circle top-right of the bulb indicating motion state. */
.fleet-pin__dot {
    position: absolute;
    top: 2px; right: 2px;
    width: 11px; height: 11px;
    border-radius: 50%;
    border: 2px solid #ffffff;
    box-sizing: border-box;
    z-index: 4;
}

/* Followed-device ring + pulse, centred on the bulb. */
.fleet-pin__track,
.fleet-pin__pulse {
    position: absolute;
    top: 18px; left: 18px;
    width: 36px; height: 36px;
    margin: -18px 0 0 -18px;
    border-radius: 50%;
    border: 2px solid #22C55E;
    z-index: 1;
}
.fleet-pin__track { animation: fleet-track 2s ease-out infinite; }
.fleet-pin__pulse { animation: fleet-pulse 1.4s ease-out infinite; }

@keyframes fleet-track {
    0%   { transform: scale(0.8); opacity: 0.9; }
    100% { transform: scale(1.6); opacity: 0; }
}
@keyframes fleet-pulse {
    0%   { transform: scale(0.7); opacity: 0.8; }
    100% { transform: scale(1.5); opacity: 0; }
}

/* Breathing glow on the bulb border while device is moving. */
.fleet-pin__glow {
    position: absolute;
    top: 0; left: 0;
    width: 36px; height: 36px;
    box-sizing: border-box;
    border-radius: 50%;
    z-index: 2;
    pointer-events: none;
    animation: fleet-glow 1.5s ease-in-out infinite;
}
@keyframes fleet-glow {
    0%, 100% {
        box-shadow: 0 0 4px 1px var(--glow), inset 0 0 3px 0 var(--glow);
        opacity: 0.55;
    }
    50% {
        box-shadow: 0 0 12px 5px var(--glow), inset 0 0 6px 1px var(--glow);
        opacity: 1;
    }
}

/* Asset name chip floating just above moving markers. */
.fleet-pin__name {
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-bottom: 4px;
    max-width: 110px;
    padding: 1px 6px;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
    font-size: 10px;
    font-weight: 600;
    border-radius: 3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    pointer-events: none;
    z-index: 5;
}
</style>
